import { useCallback, useEffect, useRef, useState } from "react";

import type { Update } from "@tauri-apps/plugin-updater";

import { isUpdateCheckPath } from "@xop/dashboard-shared";

import {
  getDesktopUpdate,
  installDesktopUpdate,
  isTauri,
  notifyNative,
  onDesktopOpenRoute,
  onDesktopWindowFocus,
  presentDesktopWindow,
  relaunchApp,
} from "./desktopBridge.js";

/**
 * State machine for the desktop self-update flow:
 *
 *   idle ─(found)─▶ available ─(install)─▶ downloading ─▶ ready ─(restart)─▶ relaunch
 *        ▲                                     │
 *        │                              (error)▼
 *   (manual check)                          error
 *   idle ─(checkNow)─▶ checking ─▶ available | uptodate(→idle after a few s)
 *
 * `idle` doubles as the dismissed / browser / resting state, so
 * <UpdateBanner/> renders nothing on `idle`.
 */
export type DesktopUpdateStatus =
  | "idle"
  | "checking"
  | "available"
  | "downloading"
  | "ready"
  | "uptodate"
  | "error";

export interface DesktopUpdate {
  status: DesktopUpdateStatus;
  version: string | null;
  notes: string | null;
  percent: number | null;
  /** Bytes downloaded so far (0 outside the downloading state). */
  downloaded: number;
  /** Total download size in bytes, or null while still unknown (the server
   * didn't advertise a Content-Length on the installer). */
  contentLength: number | null;
  /** Average transfer rate in bytes/sec since the download started, or null
   * before the first chunk. Drives the "1.8 MB/s · ~18s left" readout. */
  bytesPerSecond: number | null;
  error: string | null;
  install: () => void;
  restart: () => void;
  dismiss: () => void;
  /** Manual check (tray "Check for Updates…"): shows checking + up-to-date feedback. */
  checkNow: () => void;
  /** Error-state primary action: retry the install if an update is pending,
   * else re-run a manual check (a check that FAILED has no pending update). */
  retry: () => void;
}

const DEFAULT_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const UPTODATE_TOAST_MS = 5000;
/** Don't re-hit the updater feed more than once every 5 min on window focus —
 * alt-tabbing back and forth shouldn't be a request storm. */
const FOCUS_CHECK_COOLDOWN_MS = 5 * 60 * 1000;

export interface UseDesktopUpdateOptions {
  checkIntervalMs?: number;
  /** Minimum gap between focus-triggered re-checks. Injectable for tests. */
  focusCooldownMs?: number;
}

export function useDesktopUpdate({
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
  focusCooldownMs = FOCUS_CHECK_COOLDOWN_MS,
}: UseDesktopUpdateOptions = {}): DesktopUpdate {
  const [status, setStatus] = useState<DesktopUpdateStatus>("idle");
  const [version, setVersion] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [downloaded, setDownloaded] = useState(0);
  const [contentLength, setContentLength] = useState<number | null>(null);
  const [bytesPerSecond, setBytesPerSecond] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateRef = useRef<Update | null>(null);
  // The version the user clicked "Later" on. Per-VERSION, not a boolean: the
  // desktop app is close-to-tray and lives for days, so a process-lifetime
  // "dismissed" flag silenced the silent check (and even the 6-hourly one)
  // until the app was fully quit — one "Later" and the NEXT release never
  // surfaced either. Dismissing 0.1.91 must not hide 0.1.92.
  const dismissedVersionRef = useRef<string | null>(null);
  const busyRef = useRef(false);
  /** When the last check started, so re-opening the window can't hammer the
   * updater endpoint on every focus flicker. */
  const lastCheckAtRef = useRef(0);
  // The version we've already raised a desktop interruption for, so the
  // 6-hourly background re-check doesn't re-bounce the dock every cycle.
  const announcedVersionRef = useRef<string | null>(null);

  const adoptUpdate = (update: Update) => {
    updateRef.current = update;
    setVersion(update.version ?? null);
    const body = (update.body ?? "").trim();
    setNotes(body.length > 0 ? body : null);
  };

  // "When a new update arrives" — treat a freshly-discovered version like an
  // incoming notification: fire the native banner AND bring the window
  // forward, once per version. Only the SILENT background check calls this;
  // a manual "Check for Updates…" deliberately doesn't (the user is already
  // looking, so stealing focus would be obnoxious).
  const announceUpdate = (update: Update) => {
    const v = update.version ?? null;
    if (!v || announcedVersionRef.current === v) return;
    announcedVersionRef.current = v;
    void notifyNative({
      title: "Update available",
      body: `XOP Command Center ${v} is ready to install.`,
    });
    void presentDesktopWindow();
  };

  // Silent background check: only promotes idle/uptodate → available, never
  // shows the "checking"/"up to date" feedback (no noise on launch). A check
  // FAILURE is logged (inside getDesktopUpdate) and swallowed here — the auto
  // check must never pop an error modal at the user unprompted.
  const check = useCallback(async () => {
    if (!isTauri() || busyRef.current) return;
    lastCheckAtRef.current = Date.now();
    let update: Update | null;
    try {
      update = await getDesktopUpdate();
    } catch {
      /* check failed — already warn-logged; stay silent on the auto path */
      return;
    }
    if (!update) return;
    // The check itself always runs (so a NEW version is found even after a
    // "Later"); only the *surfacing* honours the dismissal, and only for the
    // exact version that was dismissed.
    if (dismissedVersionRef.current === (update.version ?? null)) return;
    adoptUpdate(update);
    setStatus((s) => (s === "idle" || s === "uptodate" ? "available" : s));
    announceUpdate(update);
  }, []);

  // Manual check (tray menu / future in-app button): surfaces progress and a
  // confirming "you're on the latest" toast when there's nothing new.
  const checkNow = useCallback(() => {
    if (!isTauri() || busyRef.current) return;
    // An explicit ask overrides a previous "Later" — including the one that
    // arrives as the release email's deep link.
    dismissedVersionRef.current = null;
    lastCheckAtRef.current = Date.now();
    setError(null);
    setStatus("checking");
    void (async () => {
      let update: Update | null;
      try {
        update = await getDesktopUpdate();
      } catch (e) {
        // A manual check that FAILED must say so — NOT masquerade as "up to
        // date" (the old bug). Surface the real reason in the error modal.
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
        return;
      }
      if (update) {
        adoptUpdate(update);
        setStatus("available");
        return;
      }
      // Up to date — show the running version, then auto-dismiss.
      let cur: string | null = null;
      try {
        const { getVersion } = await import("@tauri-apps/api/app");
        cur = await getVersion();
      } catch {
        /* version unavailable — show a generic confirmation */
      }
      setVersion(cur);
      setNotes(null);
      setStatus("uptodate");
      setTimeout(() => {
        setStatus((s) => (s === "uptodate" ? "idle" : s));
      }, UPTODATE_TOAST_MS);
    })();
  }, []);

  useEffect(() => {
    void check();
    if (!checkIntervalMs) return undefined;
    const id = setInterval(() => void check(), checkIntervalMs);
    return () => clearInterval(id);
  }, [check, checkIntervalMs]);

  // Tray "Check for Updates…" → the Rust shell emits "menu://check-update".
  useEffect(() => {
    if (!isTauri()) return undefined;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      try {
        const { listen } = await import("@tauri-apps/api/event");
        const fn = await listen("menu://check-update", () => checkNow());
        if (cancelled) fn();
        else unlisten = fn;
      } catch {
        /* event API unavailable (older shell / browser) — ignore */
      }
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [checkNow]);

  // RE-OPENING the app must re-check. The mount effect above fires exactly
  // once per webview, but the app is single-instance + close-to-tray, so it
  // stays alive for days: "opening" it is a window SHOW, not a remount, and
  // the only other trigger was the 6-hourly interval. That is the 2026-08-07
  // bug — you click the release email, the window comes forward, and nothing
  // says an update is waiting until you use the tray menu. Re-check whenever
  // the window regains focus, throttled so alt-tabbing doesn't hammer the
  // updater feed. Silent (`check`), so a focus on an up-to-date app is
  // invisible.
  useEffect(() => {
    if (!isTauri()) return undefined;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      const fn = await onDesktopWindowFocus(() => {
        if (Date.now() - lastCheckAtRef.current < focusCooldownMs) return;
        void check();
      });
      if (cancelled) fn();
      else unlisten = fn;
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [check, focusCooldownMs]);

  // The release email's CTA (`DEEP_LINK_UPDATE_PATH`) — an explicit "I came
  // here to update". Runs the VISIBLE check so the user gets feedback either
  // way, and `checkNow` clears any earlier "Later" for this version. Warm
  // opens arrive as `xop://open-route`; a COLD start is already covered by
  // the mount check above (and `useDeepLinkRouting` drains the stashed URL).
  useEffect(() => {
    if (!isTauri()) return undefined;
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      const fn = await onDesktopOpenRoute((path) => {
        if (isUpdateCheckPath(path)) checkNow();
      });
      if (cancelled) fn();
      else unlisten = fn;
    })();
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [checkNow]);

  const install = useCallback(() => {
    const update = updateRef.current;
    if (!update || busyRef.current) return;
    busyRef.current = true;
    setStatus("downloading");
    setPercent(0);
    setDownloaded(0);
    setContentLength(null);
    setBytesPerSecond(null);
    setError(null);
    // Wall-clock start so we can report an average transfer rate (and from it
    // an ETA). performance.now() is monotonic + available in the webview.
    const startedAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    void (async () => {
      try {
        await installDesktopUpdate(update, (p) => {
          setPercent(p.percent);
          setDownloaded(p.downloaded);
          setContentLength(p.contentLength);
          const now =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          const elapsedSec = (now - startedAt) / 1000;
          setBytesPerSecond(
            elapsedSec > 0 && p.downloaded > 0 ? p.downloaded / elapsedSec : null,
          );
        });
        setPercent(100);
        setStatus("ready");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
      } finally {
        busyRef.current = false;
      }
    })();
  }, []);

  const restart = useCallback(() => {
    void relaunchApp();
  }, []);

  // Error-modal primary action. An install failure has a pending update on
  // hand → retry the install; a *check* failure has none → re-run the check.
  const retry = useCallback(() => {
    if (updateRef.current) install();
    else checkNow();
  }, [install, checkNow]);

  const dismiss = useCallback(() => {
    dismissedVersionRef.current = updateRef.current?.version ?? null;
    setError(null);
    setStatus("idle");
  }, []);

  return {
    status,
    version,
    notes,
    percent,
    downloaded,
    contentLength,
    bytesPerSecond,
    error,
    install,
    restart,
    dismiss,
    checkNow,
    retry,
  };
}
